'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.js';
import {
  ArticleThemesEnum,
  ArticleTopicsEnum,
  ArticleTypesEnum,
} from '@sola/contentful-model/lib/taxonomy-values.js';
import { trackSelectContent } from '@sola/cookies/components/datalayer.js';
import { ARTICLE_URL_PARAMS } from '@sola/schema/lib/schema.js';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.js';
import {
  useListArticlesQuery,
  useListRelatedArticlesQuery,
} from '@sola/skippy-search-sdk/lib/queries.js';
import { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.js';

import { getBaseSizeProps } from '../../utils/text-utils';
import { CardList } from '../card-list/card-list';
import { ArticleCardSkeleton, Card } from '../card/card';
import { FeatureBase } from '../feature-base/feature-base';
import { Pagination } from '../pagination/pagination';

const queryClient = new QueryClient();

export const ArticleListProps = z.object({
  seoLevel: z.number().int().optional(),
  articleType: ArticleTypesEnum.optional(),
  country: z.string().optional(),
  theme: ArticleThemesEnum.optional(),
  topic: ArticleTopicsEnum.optional(),
  excludeContentfulEntryID: z.string().optional(),
  onArticleLoad: z
    .function({
      input: [
        z.object({
          totalCount: z.number(),
        }),
      ],
    })
    .optional(),
  variant: z.enum(['default', 'related']).optional(),
});
export type ArticleListProps = z.infer<typeof ArticleListProps>;

const ArticleListSkeleton: React.FC<{
  itemCount?: number;
  columns?: number;
  columnsMd?: number;
  columnsLg?: number;
}> = ({ itemCount = 12, columns = 1, columnsMd = 3, columnsLg = 4 }) => {
  return (
    <CardList
      columns={columns}
      columnsMd={columnsMd}
      columnsLg={columnsLg}
      classNameGap="gap-y-roomy-xs gap-x-comfy-xl"
    >
      {Array.from({ length: itemCount }, (_, index) => (
        <ArticleCardSkeleton key={`loading-article-skeleton-${index}`} />
      ))}
    </CardList>
  );
};

const InternalArticleList: React.FC<ArticleListProps> = ({
  seoLevel = 3,
  country,
  theme,
  topic,
  onArticleLoad,
  articleType,
  excludeContentfulEntryID,
  variant = 'default',
}) => {
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const { replace } = useRouter();
  const pathname = usePathname();
  const params = useMemo(
    () => new URLSearchParams(searchParams?.toString()),
    [searchParams],
  );
  const [itemOffset, setItemOffset] = useState<number>(0);
  const sortBy =
    params?.get(ARTICLE_URL_PARAMS.SORTBY) ||
    (variant === 'default' ? 'desc' : undefined);
  const limitValue =
    variant === 'related' ? 3 : Number(params?.get(ARTICLE_URL_PARAMS.LIMIT));

  const perPageValue = limitValue || 12;
  const offsetValue = limitValue || 0;

  const columnLayout = {
    columns: 1,
    columnsMd: 3,
    columnsLg: variant === 'related' ? 3 : 4,
  };

  const sortByItemsObject = {
    featured: {
      text: t('skippy.article.sort.featured'),
      value: 'promotedContent',
    },
    descending: {
      text: t('skippy.article.sort.newest'),
      value: 'desc',
    },
    ascending: {
      text: t('skippy.article.sort.oldest'),
      value: 'asc',
    },
  } as const;

  let sortDir;
  if (sortBy === sortByItemsObject.ascending.value) {
    sortDir = sortByItemsObject.ascending.value;
  } else if (sortBy === sortByItemsObject.descending.value) {
    sortDir = sortByItemsObject.descending.value;
  }

  const filterString = {
    'taxonomy.articleType.keyword': articleType || undefined,
    'taxonomy.articleTopics.keyword':
      topic || params?.get(ARTICLE_URL_PARAMS.TOPIC) || undefined,
    'taxonomy.articleThemes.keyword':
      theme || params?.get(ARTICLE_URL_PARAMS.THEME) || undefined,
    'taxonomy.programCountry.keyword':
      country || params?.get(ARTICLE_URL_PARAMS.COUNTRY) || undefined,
    'taxonomy.other.keyword':
      params?.get(ARTICLE_URL_PARAMS.SORTBY) ===
      sortByItemsObject.featured.value
        ? params?.get(ARTICLE_URL_PARAMS.SORTBY)
        : undefined,
  };

  const relatedFilterString = {
    'taxonomy.articleType.keyword': articleType || undefined,
    'taxonomy.articleTopics.keyword':
      topic || params?.get(ARTICLE_URL_PARAMS.TOPIC) || undefined,
    'taxonomy.articleThemes.keyword':
      theme || params?.get(ARTICLE_URL_PARAMS.THEME) || undefined,
    'taxonomy.programCountry.keyword':
      country || params?.get(ARTICLE_URL_PARAMS.COUNTRY) || undefined,
  };

  const mustNotString = excludeContentfulEntryID
    ? {
        'contentfulEntryID.keyword': excludeContentfulEntryID,
      }
    : {};

  const articleQuery = useListArticlesQuery({
    perPage: perPageValue,
    offset: Number(searchParams?.get('page')) * perPageValue || itemOffset,
    filters: JSON.stringify(filterString),
    must_not: JSON.stringify(mustNotString),
    sort: sortDir ? 'datePublished' : undefined,
    sortDir,
  });

  const relatedArticlesQuery = useListRelatedArticlesQuery({
    perPage: perPageValue,
    should: JSON.stringify(relatedFilterString),
    must_not: JSON.stringify(mustNotString),
  });

  const {
    data: articleData,
    isLoading,
    isError,
  } = variant === 'related' ? relatedArticlesQuery : articleQuery;
  const articles = articleData?.items;
  const totalCount = articleData?.totalCount || 0;
  const shouldShowPagination = variant === 'default';

  const getEyebrow = (result: SearchResultProps) => {
    if (result.taxonomyTopic) {
      return t(
        `skippy.taxonomy.articleTopics.${result.taxonomyTopic[0] as ArticleTopicsEnum}.name`,
      );
    }
    if (result.taxonomyArticleTheme) {
      return t(
        `skippy.taxonomy.articleThemes.${result.taxonomyArticleTheme[0] as ArticleThemesEnum}.name`,
      );
    }
    if (result.taxonomyArticleType) {
      return t(
        `skippy.taxonomy.articleTypes.${result.taxonomyArticleType as ArticleTypesEnum}.singularName`,
      );
    }
    return undefined;
  };

  const handlePageChange = (page: number) => {
    params.set(ARTICLE_URL_PARAMS.PAGE, page.toString());
    replace(`${pathname}?${params.toString()}`);
    setItemOffset(page * perPageValue);
  };

  useEffect(() => {
    setItemOffset(
      offsetValue * (Number(params?.get(ARTICLE_URL_PARAMS.PAGE)) || 0),
    );
  }, [offsetValue, params]);

  useEffect(() => {
    if (onArticleLoad && !isLoading) {
      onArticleLoad({ totalCount });
    }
  }, [totalCount, isLoading, onArticleLoad]);

  if (isLoading) {
    return (
      <ArticleListSkeleton
        itemCount={perPageValue}
        {...(variant === 'related' && { ...columnLayout })}
      />
    );
  }

  if (isError) {
    return (
      <div className="ArticleList flex flex-col gap-comfy-sm">
        <div className="text-center p-comfy-xl">
          <Alert
            variant="negative"
            message={t(
              'skippy.articles.errorLoading',
              'Error loading articles. Please try again.',
            )}
          />
        </div>
      </div>
    );
  }

  if (!articles?.length) {
    return (
      <div className="ArticleList flex flex-col gap-comfy-sm">
        <FeatureBase
          title={t('skippy.article.noResultsTitle')}
          body={
            <Trans
              i18nKey="skippy.article.noResultsBody"
              components={{
                1: <p />,
                2: (
                  <Link
                    href={`${articlesUrlBase[articleType as ArticleTypesEnum]}?tab=theme`}
                  />
                ),
                3: (
                  <Link
                    href={`${articlesUrlBase[articleType as ArticleTypesEnum]}?tab=search`}
                  />
                ),
              }}
            />
          }
        />
      </div>
    );
  }

  const uniqueArticles = articles.filter(
    (a, i, arr) =>
      arr.findIndex((b) => b.contentfulEntryID === a.contentfulEntryID) === i,
  );

  return (
    <div className="ArticleList flex flex-col gap-comfy-sm">
      <CardList {...columnLayout} classNameGap="gap-y-roomy-xs gap-x-comfy-xl">
        {uniqueArticles?.map((article) => {
          const cardProps = extractArgsSearchResult(article);
          return (
            <Card
              key={article.contentfulEntryID}
              {...getBaseSizeProps(4)}
              seoLevel={seoLevel}
              title={article.pageTitle}
              subtitle={getEyebrow(cardProps)}
              media={cardProps.media}
              body={<p>{article.summary}</p>}
              primaryCTA={
                article.path
                  ? {
                      link: article.path,
                      title: 'Read more',
                    }
                  : undefined
              }
              variant="post"
              hasPlaceholder
              alt
              onClick={() =>
                trackSelectContent(
                  article.contentfulEntryID!,
                  article.pageTitle!,
                  article.contentfulType!,
                  [
                    ...(article.taxonomy?.articleTopics ?? []),
                    ...(article.taxonomy?.articleThemes ?? []),
                  ],
                )
              }
            />
          );
        }) || []}
      </CardList>
      {shouldShowPagination && articleData && articleData.totalCount && (
        <Pagination
          itemsPerPage={perPageValue}
          totalItems={articleData.totalCount}
          onPageChange={(page) => handlePageChange(page)}
        />
      )}
    </div>
  );
};

export const ArticleList: React.FC<ArticleListProps> = (props) => {
  return (
    <Suspense fallback={<ArticleListSkeleton />}>
      <QueryClientProvider client={queryClient}>
        <InternalArticleList {...props} />
      </QueryClientProvider>
    </Suspense>
  );
};
